import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination } from "@/hq/HQPrimitives";

const STATUS_TONE = {
  active:    { bg: "#EBFBF2", fg: "#0FA958", bd: "#B7EBCF" },
  paused:    { bg: "#FFF9E6", fg: "#8A6D00", bd: "#F5E4B0" },
  cancelled: { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
};

function StatusPill({ value }) {
  const c = STATUS_TONE[value] || STATUS_TONE.cancelled;
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
      {value || "—"}
    </span>
  );
}

export default function HQOptimiseMonitoring() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [rescanning, setRescanning] = useState(null);

  const load = () => {
    setData(null);
    const params = { page, page_size: 25 };
    if (status) params.status = status;
    return hqApi.get("/optimise/monitoring", { params })
      .then((r) => setData(r.data))
      .catch(() => setErr("Something went wrong loading monitored websites."));
  };

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (status) params.status = status;
    hqApi.get("/optimise/monitoring", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading monitored websites."));
    return () => { alive = false; };
  }, [page, status]);

  const items = data?.items || [];

  const rescan = async (profileId) => {
    setRescanning(profileId);
    try {
      await hqApi.post(`/optimise/monitoring/${profileId}/rescan`);
      await load();
    } catch { /* toast optional */ }
    finally { setRescanning(null); }
  };

  return (
    <div className="space-y-4" data-testid="hq-optimise-monitoring">
      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-[#F6F5F2] p-1" data-testid="hq-monitoring-status-filter">
            {[["", "All"], ["active", "Active"], ["paused", "Paused"], ["cancelled", "Cancelled"]].map(([val, label]) => (
              <button
                key={val || "all"}
                type="button"
                onClick={() => { setStatus(val); setPage(1); }}
                data-testid={`hq-monitoring-status-${val || "all"}`}
                className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors ${
                  status === val ? "bg-black text-white" : "text-black/60 hover:text-black"
                }`}
              >{label}</button>
            ))}
          </div>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-monitoring-total">
            {data ? `${data.total} website${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {err ? (
          <div className="p-6 text-sm text-[#C1350B]">{err}</div>
        ) : !data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No monitored websites yet" hint="Once a customer subscribes to Orange Optimise, their website will appear here." testId="hq-monitoring-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Website</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Current</th>
                  <th className="text-left px-5 py-3 font-bold">Previous</th>
                  <th className="text-left px-5 py-3 font-bold">Cadence</th>
                  <th className="text-left px-5 py-3 font-bold">Next scan</th>
                  <th className="text-left px-5 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-monitoring-row-${p.id}`}>
                    <td className="px-5 py-3 font-bold break-all">
                      <Link to={`/hq/optimise/monitoring/${p.id}`} className="hover:text-[#FF6A00]" data-testid={`hq-monitoring-view-${p.id}`}>
                        {p.website_id?.slice(0, 8) || "—"}
                      </Link>
                    </td>
                    <td className="px-5 py-3"><StatusPill value={p.status} /></td>
                    <td className="px-5 py-3 font-black">{p.current_score ?? "—"}</td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A]">{p.last_score ?? "—"}</td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A]">
                      {p.frequency_hours ? `every ${p.frequency_hours}h` : "—"}
                    </td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {p.next_scan_at ? new Date(p.next_scan_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => rescan(p.id)}
                        disabled={rescanning === p.id}
                        data-testid={`hq-monitoring-rescan-${p.id}`}
                        className="px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-white text-[10px] font-black tracking-[0.2em] uppercase hover:border-[#FF6A00] disabled:opacity-50"
                      >
                        {rescanning === p.id ? "Rescanning…" : "Rescan"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-monitoring-page" />}
    </div>
  );
}
